// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngUtilities from "@bg/App/Components/Common/XngUtilities";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { checkoutPages } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { color } from "@bg/XDL/Utilities";
import xScreen from "@bg/XDL/Utilities/xScreen";
import SessionTimeoutModal from "@bgs/App/Components/Common/SessionTimeoutModal";
import { LinkPresentationProps } from "@bgs/MobiusOverrides/Link/Link";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as ContainerProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        isSticky: state.xHeader.isSticky,
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        // XNG-Changes
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
        warehousePhone: getSession(state).pickUpWarehouse?.phone,
        isOrderConfirmationPage: getCurrentPage(state).type === "OnePageCheckoutConfirmationPage",
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory &
    HasShellContext;

export interface HeaderStyles {
    header?: ContainerProps;
    logoLinksSearchWrapper?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    logoWrapper?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    mainNavigationWrapper?: InjectableCss;
    secondaryNavigationPage?: ContainerProps;
    logoLinksSearchPage?: ContainerProps;
    mainNavigationPage?: ContainerProps;
    viewDashboardLink?: LinkPresentationProps;
    // XNG-Changes
    checkoutHeader?: InjectableCss;
    branchPhone?: TypographyPresentationProps;
    checkoutHeaderPage?: ContainerProps;
    checkoutHeaderInnerContainer?: InjectableCss;
    branchPhoneLink?: LinkPresentationProps;
    branchPhoneText?: TypographyPresentationProps;
}

export const headerStyles: HeaderStyles = {
    header: {
        as: "header",
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            position: relative;
            z-index: 1286;
            &.order-confirmation-header {
                margin-bottom: 16px;
            }
        `,
    },
    mainNavigationWrapper: {
        css: css`
            display: block;
            width: 100%;
            background-color: ${getColor("common.white")};
            box-shadow: 0 2px 11px 0 rgba(53, 65, 94, 0.25);

            ${({ isCMS }: any) =>
                !isCMS &&
                css`
                    height: 55px;
                `}

            ${({ isSticky }: any) =>
                isSticky &&
                `
                display: none;
            `}
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    secondaryNavigationWrapper: {
        css: css`
            display: block;
            width: 100%;
            background-color: ${getColor("text.main")};
            ${({ isSticky }: any) =>
                isSticky &&
                `
            display: none;
            `}
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    logoLinksSearchWrapper: {
        css: css`
            display: block;
            width: 100%;
        `,
    },
    viewDashboardLink: {
        css: css`
            &&,
            &&:hover,
            &&:focus {
                color: ${getColor("text")};
            }
        `,
        typographyProps: {
            color: "secondary",
            size: fontConfig.fontSize.xSmall,
            css: css`
                color: ${getColor("text")} !important;
            `,
        },
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: ${fontConfig.fontSize.xxSmall}px;
            padding-left: 15px;
            display: none;
        `,
    },
    // XNG-Changes
    checkoutHeader: {
        css: css`
            background-color: ${getColor("text.main")};

            ${({ isCMS }: any) =>
                !isCMS &&
                css`
                    position: fixed;
                    height: 64px;
                    width: 100%;
                    > main {
                        height: 100%;
                    }
                `}
        `,
    },
    checkoutHeaderInnerContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: 24px;
            padding: 8px 0;
            height: 100%;
            > div:last-child {
                width: auto;
            }
        `,
    },
    branchPhone: {
        css: css`
            display: flex;
            gap: 4px;
            flex-wrap: nowrap;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    branchPhoneLink: {
        typographyProps: {
            css: css`
                &&& {
                    color: ${color("common.white")};
                    &:hover {
                        color: ${color("text.link")};
                    }
                }
                white-space: nowrap;
            `,
        },
    },
    branchPhoneText: {
        color: "common.white",
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isSticky,
    shellContext,
    // XNG-Changes
    isCheckoutPage,
    warehousePhone,
    isOrderConfirmationPage,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler} {...styles.viewDashboardLink}>
                            {translate("View My Dashboard")}
                        </Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    /**
     * XNG-Changes
     * BG-2729
     *  Change structure, support different layout for checkout pages
     */
    return (
        <Page
            isHeader={true}
            {...styles.header}
            id="header"
            className={isOrderConfirmationPage ? "order-confirmation-header" : ""}
        >
            {(!isCheckoutPage || (shellContext.isInShell && shellContext.isEditing)) && (
                <>
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                    <StyledWrapper {...styles.secondaryNavigationWrapper} {...({ isSticky } as any)}>
                        <Page {...styles.secondaryNavigationPage}>
                            <Zone contentId={id} zoneName="SecondaryNavigation" />
                        </Page>
                    </StyledWrapper>
                    <StyledWrapper {...styles.logoLinksSearchWrapper} {...({ isSticky } as any)}>
                        <Zone contentId={id} zoneName="LogoLinksSearch" />
                    </StyledWrapper>
                    <StyledWrapper
                        {...styles.mainNavigationWrapper}
                        {...({ isSticky, isCMS: shellContext.isInShell && shellContext.isEditing } as any)}
                    >
                        <Page {...styles.mainNavigationPage}>
                            <Zone contentId={id} zoneName="MainNavigation" />
                        </Page>
                    </StyledWrapper>
                </>
            )}
            {(isCheckoutPage || (shellContext.isInShell && shellContext.isEditing)) && (
                <StyledWrapper
                    {...styles.checkoutHeader}
                    {...{ isCMS: shellContext.isInShell && shellContext.isEditing }}
                >
                    <Page {...styles.checkoutHeaderPage}>
                        <StyledWrapper {...styles.checkoutHeaderInnerContainer}>
                            {shellContext.isInShell && shellContext.isEditing && (
                                <Typography color="common.white">Checkout Pages Header</Typography>
                            )}
                            <Zone contentId={id} zoneName="Logo" />
                            {warehousePhone && (
                                <StyledWrapper {...styles.branchPhone}>
                                    <Typography {...styles.branchPhoneText}>{translate("Support:")}</Typography>
                                    <Link href={`tel:${warehousePhone}`} {...styles.branchPhoneLink}>
                                        {warehousePhone}
                                    </Link>
                                </StyledWrapper>
                            )}
                            <Zone contentId={id} zoneName="CartLink" />
                        </StyledWrapper>
                    </Page>
                </StyledWrapper>
            )}
            <span ref={afterHeader} tabIndex={-1} />
            {/** XNG-Changes */}
            <SessionTimeoutModal />
            <XngUtilities />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withToaster(withHistory(Header)))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
